import React, { useEffect, useMemo, useRef, useState } from 'react'
import trash from '../../../../../assets/restaurant/images/trash.svg';
import { useTranslation } from 'react-i18next';
import { ITEM_TYPES, LOADER_TIMEOUT_TIME, SWAL_SETTINGS } from '../../../../../utils/Constants';
import Swal from 'sweetalert2';
import * as addonService from '../../../services/addons.service';
import * as menuItemsService from '../../../services/menuItem.services';
import { getTranslatedKey, globalLoader, handleOffcanvasScroll, handleServerValidations, TrimText, useDebouncing } from '../../../../../utils/commonfunction';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import Skeleton from 'react-loading-skeleton';
import { ReactSortable } from 'react-sortablejs';
import { useNavigate, useParams } from 'react-router-dom';
import RecordNotFound from '../../../shared/recordNotFound';
import ActiveOptionInfo from '../shared/ActiveOptionInfo';

/**
 * A cap field: blank means NO cap, and that is the default for every group and
 * every assigned item. Anything typed has to be a whole number of at least one
 * — a 0 would read as "no cap" to the backend (`normalizeCap`), which is the
 * opposite of what someone typing 0 means, so it is rejected here rather than
 * silently stored as unlimited.
 *
 * The empty string has to be transformed away explicitly: Yup casts `''` to
 * `NaN`, which fails `nullable()` and would make an untouched field invalid.
 */
const capSchema = (message) =>
  Yup.number()
    .transform((value, original) => (original === '' || original === null ? null : value))
    .nullable()
    .typeError(message)
    .integer(message)
    .min(1, message);

/** A cap on its way to the API: blank clears it, anything else is a number. */
const capValue = (value) => (value === '' || value == null ? null : Number(value));

export default function OptionsListing() {
    const navigate = useNavigate();
    const params = useParams();
    const addFormCloseBtn = useRef();
    const editFormCloseBtn = useRef();
    const assignFormCloseBtn = useRef();
    const assignmentItemRefs = useRef({});
    const itemToScrollRef = useRef(null);
    const { t } = useTranslation();
    const [search] = useState({});
    const [globalSearch, setGlobalSearch] = useState('');
    const [refreshList, setRefreshList] = useState(false);
    const [refreshItemsList, setRefreshItemsList] = useState(null);
    const [loader, setLoader] = useState(true);
    const [itemsLoader, setItemsLoader] = useState(true);
    const [List, setList] = useState([]);
    const [editableId, setEditableId] = useState(null);
    const [activeOption, setActiveOption] = useState(null);
    const [addSubmissionLoader, setAddSubmissionLoader] = useState(false);
    const [editSubmissionLoader, setEditSubmissionLoader] = useState(false);
    const [assignSubmissionLoader, setAssignSubmissionLoader] = useState(false);

    /** Add record form */
    const addForm = useFormik({
        initialValues: {
            addon_name: "",
            addon_name_ja: ""
        },
        validationSchema: Yup.object().shape({
            addon_name: Yup.string().trim().required(t("validation_addon_name_required")),
            addon_name_ja: Yup.string().trim().required(t("validation_addon_name_ja_required"))
        }),
        enableReinitialize: true,
        onSubmit: (values, { resetForm }) => {
            setAddSubmissionLoader(true);
            addonService.add(values).then((response) => {
                setAddSubmissionLoader(false);
                if (response.success) {
                    Swal.fire({
                        icon: "success",
                        text: response.message,
                        ...SWAL_SETTINGS,
                    });
                    resetForm();
                    addFormCloseBtn?.current?.click();
                    setRefreshList(!refreshList);
                } else {
                    Swal.fire({
                        icon: "error",
                        text: handleServerValidations(response),
                        ...SWAL_SETTINGS,
                    });
                }
            }).catch((error) => {
                setAddSubmissionLoader(false);
                console.error("Error updating profile", error);
            });
        },
    });
    /** Edit record form */
    const editForm = useFormik({
        initialValues: {
            o_id: "",
            addon_name: "",
            addon_name_ja: "",
        },
        validationSchema: Yup.object().shape({
            addon_name: Yup.string().trim().required(t("validation_addon_name_required")),
            addon_name_ja: Yup.string().trim().required(t("validation_addon_name_ja_required"))
        }),
        onSubmit: (values, { resetForm }) => {
            setEditSubmissionLoader(true);
            addonService.edit(values).then((response) => {
                setEditSubmissionLoader(false);
                if (response.success) {
                    Swal.fire({
                        icon: "success",
                        text: response.message,
                        ...SWAL_SETTINGS,
                    });
                    resetForm();
                    editFormCloseBtn?.current?.click();
                    setRefreshList(!refreshList);
                } else {
                    Swal.fire({
                        icon: "error",
                        text: handleServerValidations(response),
                        ...SWAL_SETTINGS,
                    });
                }
            }).catch((error) => {
                setEditSubmissionLoader(false);
                console.error("Error updating profile", error);
            });
        },
    });
    /** Assign items form */
    const assignForm = useFormik({
        initialValues: {
            o_id: "",
            assigned_items: [],
        },
        validationSchema: Yup.object().shape({
            assigned_items: Yup.array().of(
                Yup.object().shape({
                    price: Yup.number().when('checked', {
                        is: true,
                        then: (schema) => schema.required('Price is required').min(0, 'Price must be 0 or more').integer('Price must be a whole number'),
                        otherwise: (schema) => schema.nullable()
                    }),
                    // Only meaningful on an assigned item, and optional even
                    // then — an unchecked row's cap is never sent.
                    max_quantity: Yup.mixed().when('checked', {
                        is: true,
                        then: () => capSchema(t("validation_max_quantity_invalid")),
                        otherwise: (schema) => schema.nullable()
                    })
                })
            )
        }),
        onSubmit: (values, { resetForm }) => {
            setAssignSubmissionLoader(true);
            const formData = {
                o_id: values.o_id,
                assigned_items: values?.assigned_items?.filter(item => item.checked).map(item => ({
                    _id: item._id,
                    price: Number(item.price),
                    max_quantity: capValue(item.max_quantity)
                }))
            };
            addonService.assignItems(formData).then((response) => {
                setAssignSubmissionLoader(false);
                if (response.success) {
                    Swal.fire({
                        icon: "success",
                        text: response.message,
                        ...SWAL_SETTINGS,
                    });
                    resetForm();
                    assignFormCloseBtn?.current?.click();
                    setRefreshList(!refreshList);
                } else {
                    Swal.fire({
                        icon: "error",
                        text: handleServerValidations(response),
                        ...SWAL_SETTINGS,
                    });
                }
            }).catch((error) => {
                setAssignSubmissionLoader(false);
                console.error("Error updating profile", error);
            });
        },
    });

    const prepareGlobalSearch = (value) => {
        setGlobalSearch(value);
    };
    const searchDebounce = useDebouncing(prepareGlobalSearch);

    const handleActiveOption = (isDefault, data) => {
        if (isDefault) {
            if (params.active_option) {
                const activeRecord = data.filter(item => item?._id === params.active_option)[0];
                if (activeRecord) {
                    setActiveOption(activeRecord);
                } else {
                    setActiveOption(data[0] || null);
                }
            } else {
                setActiveOption(data[0] || null);
            }
        } else {
            setActiveOption(data || null);
        }
    };

    const isSelectedItem = (record) => {
        if (activeOption?.assigned_items?.length) {
            return activeOption.assigned_items.find(item => item?._id === record?._id) || null;
        }
        return null;
    };

    /** Getting list */
    useEffect(() => {
        setLoader(true);
        const formData = new FormData();
        if (Object.keys(search).length) {
            formData.append("search", JSON.stringify(search));
        }
        formData.append("global_search", globalSearch);
        addonService.list(formData).then(response => {
            setTimeout(()=>{setLoader(false);} , LOADER_TIMEOUT_TIME) ;
            if (response.success) {
                setList(response && response.data && response.data.list ? response.data.list : []);
                handleActiveOption(true, response.data.list);
            } else {
                Swal.fire({
                    icon: "error",
                    text: handleServerValidations(response),
                    ...SWAL_SETTINGS,
                });
            }
        }).catch(error => {
            setTimeout(()=>{setLoader(false);} , LOADER_TIMEOUT_TIME) ;
            console.log("error ====> ", error);
        });
    }, [search, refreshList, globalSearch]);

    /** Getting record details for edit */
    useEffect(() => {
        if (editableId) {
            globalLoader(true);
            addonService.details(editableId).then(response => {
                globalLoader(false);
                if (response.success) {
                    editForm.resetForm({
                        values: {
                            o_id: response?.data?._id,
                            addon_name: response?.data?.addon_name,
                            addon_name_ja: response?.data?.addon_name_ja
                        }
                    });
                } else {
                    Swal.fire({
                        icon: "error",
                        text: handleServerValidations(response),
                        ...SWAL_SETTINGS,
                    });
                }
            }).catch(error => {
                globalLoader(false);
            });
        }
    }, [editableId]);

    /** Getting items list */
    useEffect(() => {
        if (refreshItemsList !== null) {
            setItemsLoader(true);
            menuItemsService.Options(ITEM_TYPES.ITEM).then(response => {
                setItemsLoader(false);
                if (response.success) {
                    const savedOrder = new Map(
                        (activeOption?.assigned_items || []).map((item, index) => [String(item?._id), index])
                    );
                    const assignedItem = (response && response.data ? response.data : [])
                    .map(item => {
                        const existing = isSelectedItem(item);
                        return {
                            ...item,
                            checked: Boolean(existing),
                            // The override is the tax-inclusive premium the set
                            // charges, so an unassigned item defaults to the
                            // item's own gross, not its net.
                            price: existing?.price ?? item?.total_price ?? '',
                            // Blank is the default: a new assignment is uncapped
                            // until someone says otherwise.
                            max_quantity: existing?.max_quantity ?? ''
                        };
                    })
                    .sort((a, b) => {
                      const aOrder = savedOrder.get(String(a?._id));
                      const bOrder = savedOrder.get(String(b?._id));
                      if (aOrder !== undefined || bOrder !== undefined) {
                        if (aOrder === undefined) return 1;
                        if (bOrder === undefined) return -1;
                        return aOrder - bOrder;
                      }
                      const nameA = a.category_data?.name?.toLowerCase() || '';
                      const nameB = b.category_data?.name?.toLowerCase() || '';
                      return nameA.localeCompare(nameB);
                    });
                  
                    assignForm.resetForm({
                        values: {
                            o_id: activeOption?._id,
                            assigned_items: assignedItem
                        }
                    });
                } else {
                    Swal.fire({
                        icon: "error",
                        text: handleServerValidations(response),
                        ...SWAL_SETTINGS,
                    });
                }
            }).catch(error => {
                setItemsLoader(false);
                console.log("error ====> ", error);
            });
        }
    }, [refreshItemsList]);

    /** Handling the language change */
    useMemo(() => {
        document.addEventListener('language_change', function () {
            setRefreshList(!refreshList);
        });
    });

    /** Handling the delete functionality */
    const handleDelete = (record) => {
        Swal.fire({
            title: t("msg_are_you_sure"),
            text: t("btn_delete_text"),
            imageUrl: trash,
            showCancelButton: true,
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6c757d",
            confirmButtonText: t("btn_delete"),
            cancelButtonText: t("btn_cancel"),
            reverseButtons: true,
            customClass: {
                popup: 'swal-restaurant',
                confirmButton: 'btn btn-danger col',
                cancelButton: 'btn btn-secondary col'
            },
            allowOutsideClick: false
        }).then((result) => {
            if (result.isConfirmed) {
                globalLoader(true);
                addonService.deleteRecord({ o_id: record._id }).then((response) => {
                    globalLoader(false);
                    if (response.success) {
                        Swal.fire({
                            icon: "success",
                            text: response.message,
                            ...SWAL_SETTINGS,
                        });
                        setRefreshList(!refreshList);
                    } else {
                        Swal.fire({
                            icon: "error",
                            text: handleServerValidations(response),
                            ...SWAL_SETTINGS,
                        });
                    }
                }).catch(() => {
                    globalLoader(false);
                });
            }
        });
    };
    /** Handling the delete functionality */
    const handleDeleteFromAssigned = (record) => {
        Swal.fire({
            title: t("msg_are_you_sure"),
            text: t("btn_delete_text"),
            imageUrl: trash,
            showCancelButton: true,
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6c757d",
            confirmButtonText: t("btn_delete"),
            cancelButtonText: t("btn_cancel"),
            reverseButtons: true,
            customClass: {
                popup: 'swal-restaurant',
                confirmButton: 'btn btn-danger col',
                cancelButton: 'btn btn-secondary col'
            },
            allowOutsideClick: false
        }).then((result) => {
            if (result.isConfirmed) {
                globalLoader(true);
                addonService.deleteAssignedItem({ option_id: activeOption?._id, item_id: record?._id }).then((response) => {
                    globalLoader(false);
                    if (response.success) {
                        Swal.fire({
                            icon: "success",
                            text: response.message,
                            ...SWAL_SETTINGS,
                        });
                        setRefreshList(!refreshList);
                    } else {
                        Swal.fire({
                            icon: "error",
                            text: handleServerValidations(response),
                            ...SWAL_SETTINGS,
                        });
                    }
                }).catch(() => {
                    globalLoader(false);
                });
            }
        });
    };

    const selectItemsToAssign = (e, i) => {
        const allValues = assignForm.values?.assigned_items || [];
        const selectedItem = { ...allValues[i], checked: e.target.checked };
        const remainingItems = allValues.filter((_, index) => index !== i);
        const checked = remainingItems.filter((item) => item.checked);
        const unchecked = remainingItems.filter((item) => !item.checked);
        itemToScrollRef.current = e.target.checked ? selectedItem?._id : null;
        const nextValues = e.target.checked
            ? [
                ...checked,
                selectedItem,
                ...unchecked,
            ]
            : [
                ...checked,
                ...unchecked,
                selectedItem,
            ];
        assignForm.setFieldValue('assigned_items', nextValues, true);
    };

    useEffect(() => {
        const itemId = itemToScrollRef.current;
        const itemElement = itemId ? assignmentItemRefs.current[itemId] : null;
        if (!itemElement) return;

        itemElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        itemToScrollRef.current = null;
    }, [assignForm.values.assigned_items]);

    const changeActiveOption = (e, record) => {
        if (e.target.classList.contains("active_option_handler")) {
            navigate('/restaurant/menu/options/list/' + record?._id);
            handleActiveOption(false, record);
            setRefreshItemsList(!refreshItemsList);
        }
    };

    const assignmentItems = assignForm?.values?.assigned_items || [];
    const selectedAssignmentItems = [];
    const unselectedAssignmentItems = [];
    assignmentItems.forEach((record, index) => {
        (record?.checked ? selectedAssignmentItems : unselectedAssignmentItems).push({ record, index });
    });
    const renderAssignmentRow = ({ record, index }) => (
        <tr
            key={record?._id || index}
            ref={(element) => {
                if (record?._id) assignmentItemRefs.current[record._id] = element;
            }}
        >
            <td className="py-2 border-0 align-top">
                {record?.checked ? (
                    <span className="option-item-drag-handle cp"><i className="ti ti-grip-vertical"></i></span>
                ) : null}
            </td>
            <td className="py-2 border-0 align-top">
                <div className="form-check">
                    <input
                        className="form-check-input cp"
                        type="checkbox"
                        id={"menu-item" + index}
                        value={record?._id}
                        checked={record?.checked}
                        onChange={(e) => { selectItemsToAssign(e, index); }}
                    />
                    <label className="form-check-label cp" htmlFor={"menu-item" + index}>
                        {getTranslatedKey("name", record?.category_data)}
                    </label>
                </div>
            </td>
            <td className="border-0 p-2 align-top">{TrimText(getTranslatedKey("item_name", record), 20)}</td>
            <td className="border-0 p-1 align-top">
                <div className="mb-1">
                    <div className="input-group">
                        <span className="input-group-text">¥</span>
                        <input
                            type="number"
                            className={"form-control no_spring_input" + (record?.checked && assignForm?.errors?.assigned_items?.[index]?.price && assignForm?.touched?.assigned_items?.[index]?.price ? " is-invalid" : "")}
                            placeholder={t("placeholder_price")}
                            min="0"
                            disabled={!record?.checked}
                            value={record?.price}
                            onChange={(e) => {
                                const allValues = assignForm.values.assigned_items.map((item, itemIndex) =>
                                    itemIndex === index ? { ...item, price: e.target.value } : item
                                );
                                assignForm.setFieldValue('assigned_items', allValues, true);
                                assignForm.setFieldTouched(`assigned_items[${index}].price`, true, false);
                            }}
                            onBlur={() => assignForm.setFieldTouched(`assigned_items[${index}].price`, true)}
                        />
                    </div>
                    {record?.checked && assignForm?.errors?.assigned_items?.[index]?.price && assignForm?.touched?.assigned_items?.[index]?.price ? (
                        <div className="text-danger p-1" style={{ fontSize: '12px' }}>{assignForm.errors.assigned_items[index].price}</div>
                    ) : null}
                </div>
            </td>
            <td className="border-0 p-1 align-top">
                <div className="mb-1">
                    <input
                        type="number"
                        className={"form-control no_spring_input" + (record?.checked && assignForm?.errors?.assigned_items?.[index]?.max_quantity && assignForm?.touched?.assigned_items?.[index]?.max_quantity ? " is-invalid" : "")}
                        placeholder={t("placeholder_max_quantity")}
                        min="1"
                        step="1"
                        disabled={!record?.checked}
                        value={record?.max_quantity}
                        onChange={(e) => {
                            const allValues = assignForm.values.assigned_items.map((item, itemIndex) =>
                                itemIndex === index ? { ...item, max_quantity: e.target.value } : item
                            );
                            assignForm.setFieldValue('assigned_items', allValues, true);
                            assignForm.setFieldTouched(`assigned_items[${index}].max_quantity`, true, false);
                        }}
                        onBlur={() => assignForm.setFieldTouched(`assigned_items[${index}].max_quantity`, true)}
                    />
                    {record?.checked && assignForm?.errors?.assigned_items?.[index]?.max_quantity && assignForm?.touched?.assigned_items?.[index]?.max_quantity ? (
                        <div className="text-danger p-1" style={{ fontSize: '12px' }}>{assignForm.errors.assigned_items[index].max_quantity}</div>
                    ) : null}
                </div>
            </td>
        </tr>
    );
    return (
      <main id="main-content">
        <div className="inner-container">
          <div className="page-header d-flex align-items-center">
            <h4 className="heading-title-text">
              {t("label_options")} ({List.length})
            </h4>
          </div>
          <div className="card-outer-header options-toolbar d-flex justify-content-between align-items-center mb-4">
            <div className="search-box">
              <input
                type="text"
                className="form-control search-input"
                placeholder={t("input_placeholder_search")}
                onInput={(e) => {
                  searchDebounce(e.target.value);
                }}
              />
            </div>
            <button className="btn btn-primary options-toolbar-action" data-bs-toggle="offcanvas" data-bs-target="#add-form-canvas">
              <i className="ti ti-plus"></i> {t("label_add_addon_option")}
            </button>
          </div>
          <div className="option-item-wrap d-flex">
            <div className="option-item-list">
              <div className="card">
                <div className="card-body">
                  {loader ? (
                    <div className="w-100">
                      <Skeleton className="mb-2" height={50} count={5} />
                    </div>
                  ) : List.length ? (
                    <ul className="option-list-ul">
                      {List.map((item, index) => {
                        return (
                          <li
                            key={index}
                            className={"active_option_handler cp" + (activeOption?._id === item?._id ? " active" : "")}
                            onClick={(e) => {
                              changeActiveOption(e, item);
                            }}
                          >
                            <span className="active_option_handler">{getTranslatedKey("addon_name", item)}</span>
                            {/* {item?.addon_name} */}
                            <div className="option-btns d-flex gap-2">
                              <button
                                className="btn btn-icon"
                                data-bs-toggle="offcanvas"
                                data-bs-target="#edit-form-canvas"
                                onClick={() => {
                                  setEditableId(item?._id);
                                }}
                              >
                                <i className="ti ti-edit"></i>
                              </button>
                              <button
                                className="btn btn-icon"
                                onClick={() => {
                                  handleDelete(item);
                                }}
                              >
                                <i className="ti ti-trash"></i>
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <RecordNotFound heading="no_addons_found_text" />
                  )}
                </div>
              </div>
            </div>
            <div className="option-item-info">
              <ActiveOptionInfo
                activeOption={activeOption}
                getTranslatedKey={getTranslatedKey}
                handleDeleteFromAssigned={handleDeleteFromAssigned}
                listLength={List.length}
                loader={loader}
                onAssign={() => setRefreshItemsList(!refreshItemsList)}
              />
            </div>
          </div>
        </div>

        {/* Add form  */}
        <div className="offcanvas offcanvas-end right-modal-w" id="add-form-canvas" data-bs-backdrop="static" tabIndex="-1" aria-labelledby="staticBackdropLabel">
          <div className="offcanvas-header">
            <h5 className="offcanvas-title" id="offcanvasRightLabel">
              {t("label_add_addon_option")}
            </h5>
            <button
              type="button"
              className="btn btn-icon ms-auto"
              ref={addFormCloseBtn}
              data-bs-dismiss="offcanvas"
              aria-label="Close"
              onClick={() => {
                addForm.resetForm();
              }}
            >
              <i className="ti ti-x"></i>
            </button>
          </div>
          <div className="offcanvas-body">
            {/* `noValidate`: Yup owns every rule on this form, and the browser
                was answering the same `min="1"` with a bubble of its own — two
                messages for one mistake, in two wordings, one of them
                untranslated. */}
            <form onSubmit={addForm.handleSubmit} noValidate>
              <div className="mb-3">
                <label className="form-label">
                  {t("label_option_name_english")}<span>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("placeholder_option_name_english")}
                  name="addon_name"
                  value={addForm.values.addon_name}
                  onChange={addForm.handleChange}
                  onBlur={addForm.handleBlur}
                />
                {addForm.touched.addon_name && addForm.errors.addon_name ? <div className="text-danger">{t(addForm.errors.addon_name)}</div> : null}
              </div>
              <div className="mb-3">
                <label className="form-label">
                  {t("label_option_name_japanese")}<span>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("placeholder_option_name_japanese")}
                  name="addon_name_ja"
                  value={addForm.values.addon_name_ja}
                  onChange={addForm.handleChange}
                  onBlur={addForm.handleBlur}
                />
                {addForm.touched.addon_name_ja && addForm.errors.addon_name_ja ? <div className="text-danger">{t(addForm.errors.addon_name_ja)}</div> : null}
              </div>

              <div className="button-form">
                <button type="submit" className={"btn btn-primary w-100" + (addSubmissionLoader ? " btn-loader" : "")} disabled={Object.keys(addForm.errors).length > 0}>
                  {t("btn_submit")}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Edit form  */}
        <div className="offcanvas offcanvas-end right-modal-w" id="edit-form-canvas" data-bs-backdrop="static" tabIndex="-1" aria-labelledby="staticBackdropLabel">
          <div className="offcanvas-header">
            <h5 className="offcanvas-title" id="offcanvasRightLabel">
              {t("label_edit_addon_option")}
            </h5>
            <button
              type="button"
              className="btn btn-icon ms-auto"
              ref={editFormCloseBtn}
              data-bs-dismiss="offcanvas"
              aria-label="Close"
              onClick={() => {
                editForm.resetForm();
                setEditableId(null);
              }}
            >
              <i className="ti ti-x"></i>
            </button>
          </div>
          <div className="offcanvas-body">
            <form onSubmit={editForm.handleSubmit} noValidate>
              <div className="mb-3">
                <label className="form-label">
                  {t("label_option_name_english")}<span>*</span>
                </label>
                <input type="hidden" name="o_id" value={editForm?.values?.o_id} onChange={editForm.handleChange} />
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("placeholder_option_name_english")}
                  name="addon_name"
                  value={editForm.values.addon_name}
                  onChange={editForm.handleChange}
                  onBlur={editForm.handleBlur}
                />
                {editForm.touched.addon_name && editForm.errors.addon_name ? <div className="text-danger">{t(editForm.errors.addon_name)}</div> : null}
              </div>
              <div className="mb-3">
                <label className="form-label">
                  {t("label_option_name_japanese")}<span>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("placeholder_option_name_japanese")}
                  name="addon_name_ja"
                  value={editForm.values.addon_name_ja}
                  onChange={editForm.handleChange}
                  onBlur={editForm.handleBlur}
                />
                {editForm.touched.addon_name_ja && editForm.errors.addon_name_ja ? <div className="text-danger">{t(editForm.errors.addon_name_ja)}</div> : null}
              </div>

              <div className="button-form">
                <button type="submit" className={"btn btn-primary w-100" + (editSubmissionLoader ? " btn-loader" : "")} disabled={Object.keys(editForm.errors).length > 0 || !editForm.dirty}>
                  {t("btn_update")}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Assign form  */}
        <div className="offcanvas offcanvas-end" style={{ width: '600px' }} id="assign-items-canvas" data-bs-backdrop="static" tabIndex="-1" aria-labelledby="staticBackdropLabel">
          <div className="offcanvas-header">
            <h5 className="offcanvas-title" id="offcanvasRightLabel">
              {t("label_assign_items")}
            </h5>
            <button
              type="button"
              className="btn btn-icon ms-auto"
              ref={assignFormCloseBtn}
              data-bs-dismiss="offcanvas"
              aria-label="Close"
              onClick={() => { assignForm.resetForm(); }}
            >
              <i className="ti ti-x"></i>
            </button>
          </div>
          <div className="offcanvas-body d-flex flex-column p-0" style={{ overflow: 'hidden' }}>
            {itemsLoader ? (
              <div className="w-100 p-3">
                <Skeleton className="mb-2" height={50} count={5} />
              </div>
            ) : (
              <form onSubmit={assignForm.handleSubmit} className="d-flex flex-column h-100" noValidate>
                <div className="flex-grow-1 overflow-auto p-3">
                  <div className="table-responsive">
                      <table className="table option-assign-table">
                        <thead>
                          <tr>
                            <th style={{ width: "36px" }}></th>
                            <th>{t("label_category")}</th>
                          <th className='p-1'>{t("label_items")}</th>
                          <th>{t("label_price_tax_included")}</th>
                          <th style={{ width: "110px" }}>{t("label_max_quantity")}</th>
                        </tr>
                      </thead>
                        <ReactSortable
                          tag="tbody"
                          list={selectedAssignmentItems.map(({ record }) => record)}
                          setList={(newList) => assignForm.setFieldValue(
                            "assigned_items",
                            [...newList, ...unselectedAssignmentItems.map(({ record }) => record)],
                            true
                          )}
                          handle=".option-item-drag-handle"
                          ghostClass="option-item-sortable-ghost"
                          forceFallback={true}
                          fallbackOnBody={false}
                        >
                          {selectedAssignmentItems.map(renderAssignmentRow)}
                        </ReactSortable>
                        <tbody>
                          {unselectedAssignmentItems.map(renderAssignmentRow)}
                        </tbody>
                    </table>
                  </div>
                </div>
                <div className="p-3 border-top">
                  <button type="submit" className={"btn btn-primary w-100" + (assignSubmissionLoader ? " btn-loader" : "")} disabled={assignSubmissionLoader || !assignForm.dirty || !assignForm.isValid}>
                    {t("btn_submit")}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>
    );
}
